'use client';

import { beautifyPrice } from "@/utils/price";
import {
  AspectRatio,
  Badge,
  Box,
  BoxProps,
  HStack,
  IconButton,
  Separator,
  Show,
  Skeleton,
  SkeletonText,
  Stack,
  Text
} from "@chakra-ui/react";
import Image from "next/image";
import { forwardRef, useEffect } from "react";
import { I18nLink } from "../i18n/I18nLink";
import { makeProductPageUrl } from "@/utils/product";
import { getImageSrc } from "@/utils/file";
import { useCartQuery } from "@/queries/cart/useCartQuery";
import { decrementCartItemRequest, incrementCartItemRequest, removeFromCartRequest } from "@/services/cart";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { CartItem } from "@/types/cart";
import { Button } from "../ui/button";
import { useRouter } from "@/i18n/navigation";
import { closeDrawer } from "@/shared/store/drawer";
import { setCartItems } from "@/shared/store/shop";
import CartBagSvgIcon from "@/shared/icons/CartBagSvgIcon";
import { LinkButton } from "../ui/link-button";
import DeliveryTruckSvgIcon from "@/shared/icons/DeliveryTruckSvgIcon";
import MinusSvgIcon from "@/shared/icons/MinusSvgIcon";
import PlusSvgIcon from "@/shared/icons/MinusSvgIcon copy";
import CashbackBadge from "./CashbackBadge";
import { useTranslations } from "next-intl";
import { useAnalytics } from "@/hooks/useAnalytics";
import { EventProduct } from "@/lib/analytics/metrics";
import { useAuth } from "../auth/AuthProvider";

type CartProps = {} & BoxProps;

const Cart = forwardRef<HTMLDivElement, CartProps>(({ ...boxProps }, ref) => {
  const t = useTranslations();
  const router = useRouter();
  const queryClient = useQueryClient();
  const { data, isLoading } = useCartQuery();
  const { track } = useAnalytics();
  const { userId } = useAuth()
  
  const incrementCartItemMutation = useMutation({
    mutationFn: async (itemId: CartItem['id']) => {
      await incrementCartItemRequest(itemId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });

  const decrementCartItemMutation = useMutation({
    mutationFn: async (itemId: CartItem['id']) => {
      await decrementCartItemRequest(itemId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });

  const removeFromCartMutation = useMutation({
    mutationFn: async (itemId: CartItem['id']) => {
      await removeFromCartRequest(itemId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["cart"] });
    },
  });

  const CartSkeleton = () => (
    <Stack paddingInline={{ base: "16px", md: "40px" }} gap={6} maxW="xs" w="100%">
      {[1, 2, 3].map(i => (
        <HStack key={i} width="full" align="start" mb={4}>
          <Skeleton height="150px" width="120px" borderRadius="8px" />
          <Stack flex="1" gap={2}>
            <SkeletonText noOfLines={2} gap={2} height="16px" width="80%" />
            <Skeleton height="20px" width="60px" />
            <Skeleton height="36px" width="100px" mt={4} />
          </Stack>
        </HStack>
      ))}
    </Stack>
  );

  const handleOrder = () => {
    router.push('/checkout');
  
    const products: EventProduct[] = data?.items.map((item) => ({
      ...item.product,
      quantity: item.amount,
    })) as EventProduct[];
    track({
      type: 'begin_checkout',
      data: {
        userId: userId ?? 0,
        total: data?.total ?? 0,
        products
      },
    });
    closeDrawer();
  };

  useEffect(() => {
    if (data) setCartItems(data.items);
  }, [data]);

  if (isLoading) return <CartSkeleton />;

  if (!data || !data.items.length) return (
    <Stack
      paddingInline={{ base: "16px", md: "40px" }}
      gap="16px"
    >
      <Separator position={'sticky'} top={0} pb={{ base: "4px", md: "22px" }} borderColor={"stroke"} />
      <Stack gap="8px">
        <CartBagSvgIcon size={"lg"} />
        <Text textStyle="bold16" lineHeight="28px">{t('cart_empty')}</Text>
        <Text textStyle="regular14" lineHeight="28px" color="gray.600">{t('cart_empty_text')}</Text>
      </Stack>
      <LinkButton variant={"qr_secondary"} minH={"40px"} href="/shop" onClick={() => closeDrawer()}>
        {t('go_to_shopping')}
      </LinkButton>
    </Stack>
  );

  return (
    <Box
      ref={ref}
      className="product-card"
      {...boxProps}
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Box
        flex="1"
        overflowY="auto"
        minHeight={{ base: "0" }}
        mb="16px"
        paddingInline={{ base: "16px", md: "40px" }}
      >
        <Separator position={'sticky'} top={0} pb={{ base: "20px", md: "38px" }} borderColor={"stroke"} />
        {data.items.map(item => (
          <HStack align={"normal"} mb={"20px"} justify={"space-between"} key={item.id} position={"relative"}
            opacity={item.isAvailable ? 1 : 0.5}>
            <HStack gap={4} align={"start"} pointerEvents={item.isAvailable ? "auto" : "none"}>
              <AspectRatio ratio={120 / 150} width="120px" height="150px" position="relative">
                <Box asChild>
                  <Image
                    src={getImageSrc(item.giftBox ?? item.product, "main")}
                    alt={item.product?.name ?? ""}
                    fill
                    sizes="120px"
                    style={{ objectFit: "cover" }}
                    quality={30}
                  />
                </Box>
              </AspectRatio>
              <Stack flex="1" gap={"4px"} justifyContent={"space-between"} height={"100%"}>
                <Box>
                  {(item.product || item.giftBox) && (
                    <I18nLink
                      key={item.id}
                      href={
                        item.product
                          ? makeProductPageUrl(item.product)
                          : `/shop/giftboxes/${item.giftBox?.id}`
                      }
                      style={{ textDecoration: 'none' }}
                    >
                      <Text
                        className="product-name"
                        textStyle="bold14"
                        as="b"
                        lineHeight="20px"
                      >
                        {item.product?.name ?? item.giftBox?.name}
                      </Text>
                    </I18nLink>
                  )}
                  <Stack direction="row" gap={"8px"} mt={1}>
                    {item.product && (
                      <>
                        <Text textStyle={"bold12"} className="product-price">
                          {beautifyPrice(item.product.price)}
                        </Text>
                        {item.product.regularPrice != null && Number(item.product.regularPrice) > Number(item.product.price) && (
                          <Text as="del" fontSize={"12px"} className="product-price">
                            {beautifyPrice(item.product.regularPrice)}
                          </Text>
                        )}
                      </>
                    )}
                    {item.giftBox && (
                      <Text as="b" className="product-price">
                        {beautifyPrice(item.price)}
                      </Text>
                    )}
                  </Stack>
                </Box>
                {item.sizeName && (
                  <HStack>
                    <Text textStyle={"bold14"} color={"#808080"}>{t('size')}:</Text>
                    <Text textStyle={"bold14"} textTransform={"uppercase"}>{item.sizeName}</Text>
                  </HStack>
                )}
                <Box outline={"1px solid #E2E8F0"} borderRadius="none" maxW={"max-content"} h={{ base: "56px", md: "34px" }} minW={{ base: "112px", md: "92px" }}>
                  <HStack gap={"16px"} height={"100%"} alignItems="center" justify="center">
                    <IconButton
                      size="xs"
                      width="16px"
                      minH="16px"
                      onClick={() => {
                        track({
                          type: 'remove_from_cart',
                          data: {
                            from: 'Cart',
                            product: {...item.product, quantity: item.amount - 1} as EventProduct,
                          }
                        });
                        decrementCartItemMutation.mutate(item.id)
                      }}
                      disabled={
                        incrementCartItemMutation.isPending ||
                        decrementCartItemMutation.isPending ||
                        !item.isAvailable ||
                        (item.amount <= 1)
                      }
                      variant="plain"
                      aria-label="Decrease"
                    >
                      <MinusSvgIcon />
                    </IconButton>
                    <Text textStyle="bold16" lineHeight="16px" color="gray.solid">{item.amount}</Text>
                    <IconButton
                      size="xs"
                      width="16px"
                      minH="16px"
                      onClick={() => {
                        track({
                          type: 'add_to_cart',
                          data: {
                            from: 'Cart',
                            product: {...item.product, quantity: item.amount + 1} as EventProduct,
                          }
                        });
                        incrementCartItemMutation.mutate(item.id)
                      }}
                      disabled={incrementCartItemMutation.isPending || decrementCartItemMutation.isPending || !item.isAvailable}
                      variant="plain"
                      aria-label="Increase"
                    >
                      <PlusSvgIcon />
                    </IconButton>
                  </HStack>
                </Box>
              </Stack>
            </HStack>
            {!item.isAvailable && (
              <Badge colorPalette="red" position="absolute" top={2} left={2} zIndex={1}>
                {t('unavailable')}
              </Badge>
            )}
            {/* <CloseButton
            onClick={() => removeFromCartMutation.mutate(item.id)}
            size="md"
            variant="plain"
            colorPalette="red"
            aria-label="Remove item from cart"
            loading={removeFromCartMutation.isPending}
            style={{ pointerEvents: "auto" }}
          /> */}
            <Button
              variant="plain"
              color="text.secondary_light"
              p={0}
              height="auto"
              onClick={() => {
                track({
                  type: 'remove_from_cart',
                  data: {
                    from: 'Cart',
                    product: {...item.product, quantity: item.amount} as EventProduct,
                  }
                });
                removeFromCartMutation.mutate(item.id)
              }}
              alignSelf={"flex-start"}
              size={undefined}
            >
              {t('remove')}
            </Button>
          </HStack>
        ))}
      </Box>
      {/* прикрепленная кнопка заказа снизу */}
      <Show when={!!data?.total}>
        <Stack
          position="sticky"
          gap="10px"
          left={0}
          bottom={0}
          width="100%"
          bg="white"
          zIndex={10}
          boxShadow={{ base: "0 -2px 58px rgba(0,0,0,0.3)", md: "none" }}
          paddingTop={{ base: "10px", md: "0px" }}
          paddingBottom={{ base: "16px", md: "35px" }}
          paddingInline={{ base: '10px', md: '40px' }}
        >
          <HStack h={"40px"} py={"6px"} px={"16px"} bg={"#F5F5F5"} gap={"10px"}>
            <DeliveryTruckSvgIcon size={"lg"} />
            {data.freeShippingRemaining === 0 ? (
              <Text textStyle={{ base: "regular14" }}>{t('free_delivery')}</Text>
            ) : (
              <Text
                textStyle={{ base: "regular14" }}>{t('amount_to_free_delivery', { amount: beautifyPrice(data.freeShippingRemaining) })}</Text>
            )}
          </HStack>
          {/* // TODO: заменить на реальный рассчет бонусов за товар */}
          <CashbackBadge amount={5} />
          <Button
            asChild
            onClick={handleOrder}
            loading={isLoading}
            disabled={isLoading}
            loadingText={t('order_loading')}
            colorScheme="blue"
            width="100%"
            height={{ base: "56px", md: "40px" }}
            textStyle={"bold14"}
            borderRadius={"none"}
            size={"xl"}
          >
            <I18nLink href="/checkout">{t('go_to_payment')} ({beautifyPrice(data.total)})</I18nLink>
          </Button>
        </Stack>
      </Show>
    </Box>
  );
});

Cart.displayName = "Cart";
export default Cart;